/**
 * Turns the commands `legalCommands` returns into things a person can read.
 *
 * The panel never decides what is on offer — it renders this list, and this
 * list is exactly what the engine said was legal, one option per command. So
 * "the panel offers exactly the legal options and nothing else" is true by
 * construction rather than by discipline, and `describe.test.ts` keeps it that
 * way by checking the two lists match in every state a game reaches.
 */
import {
  MANIFESTS,
  bonusTileVp,
  legalCommands,
  occupationVp,
  type ActionKind,
  type Command,
  type PlayerId,
  type ProvinceId,
  type View,
} from '@trajan/engine';

/**
 * Which part of the screen is responsible for offering an option. `builder`
 * means a family `legalCommands` deliberately narrows — the seaport's display
 * and ship sets — where the panel offers the whole family through a selector
 * rather than the one representative payload the engine listed.
 */
export type Owner = 'panel' | 'mat' | 'setup' | 'builder';

export interface Option {
  /** Unique within a list, and stable while the state is. */
  key: string;
  command: Command;
  owner: Owner;
  /** Main options, or the skip/end-turn pair that closes a turn. */
  group: 'action' | 'turn';
  title: string;
  detail?: string;
  /** Victory points the option is worth outright, where the rules make it plain. */
  vp?: number;
  /**
   * False while an option is rendered by the generic fallback because its own
   * panel belongs to a later slice. It is still offered — never hidden — just
   * described plainly rather than laid out for its action.
   */
  ready: boolean;
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];
const A = (s: string) => (/^[aeiou]/i.test(s) ? 'an' : 'a');

/** Every legal command for this seat, described. Same length, same order. */
export function describeCommands(view: View, seat: PlayerId): Option[] {
  return legalCommands(view, seat).map((command, i) => describe(command, view, seat, i));
}

function describe(command: Command, view: View, seat: PlayerId, i: number): Option {
  const player = view.players[seat];
  switch (command.type) {
    case 'SelectTray':
      return {
        key: `tray-${command.tray}`,
        command,
        owner: 'mat',
        group: 'action',
        title: `Pick up the ${MANIFESTS.trayActions[command.tray]} tray`,
        ready: true,
      };

    case 'ArrangeMarkers':
      // The mat is where this happens, as it is for picking a tray up: the
      // payload is a whole arrangement of twelve markers, which is a thing to
      // do with your hands and not a row to press.
      return {
        key: 'arrange',
        command,
        owner: 'mat',
        group: 'action',
        title: 'Arrange your markers',
        ready: true,
      };

    case 'ChooseInitialTrajanTiles':
      return {
        key: `initial-${command.picks.map((p) => p.stack).join('-')}`,
        command,
        owner: 'setup',
        group: 'action',
        title: `Take tiles from stacks ${command.picks.map((p) => p.stack + 1).join(', ')}`,
        ready: false,
      };

    case 'TakeAction':
      return describeAction(command, view, seat, i);

    case 'SkipAction': {
      const action = view.phase.kind === 'awaitAction' ? view.phase.action : 'this';
      return {
        key: 'skip',
        command,
        owner: 'panel',
        group: 'turn',
        title: `Skip the ${action} action`,
        detail: 'takes nothing, and no extra action follows',
        ready: true,
      };
    }

    case 'EndTurn':
      return {
        key: 'end-turn',
        command,
        owner: 'panel',
        group: 'turn',
        title: 'End turn',
        ready: true,
      };

    case 'AccomplishTrajanTile': {
      const slot = view.phase.kind === 'awaitTrajanDecision' ? view.phase.slot : 0;
      const tile = player?.trajanSlots[slot] ?? null;
      if (!command.accept) {
        return {
          key: 'trajan-decline',
          command,
          owner: 'panel',
          group: 'turn',
          title: 'Leave the tile where it is',
          detail: 'it stays in its slot, and the Arch does not move',
          ready: true,
        };
      }
      return {
        key: `trajan-accept-${command.plus2Space ?? 'plain'}`,
        command,
        owner: 'panel',
        group: 'action',
        title: command.plus2Space
          ? `Accomplish it, “+2” on your ${command.plus2Space} space`
          : `Accomplish the ${trajanName(tile?.kind ?? '')} tile`,
        ...(tile
          ? {
              detail: command.plus2Space
                ? `a second ${command.plus2Space} extra action, whenever you spend a ${command.plus2Space} tile`
                : `slot ${ROMAN[slot]} — ${trajanReward(tile)}`,
              vp: tile.vp,
            }
          : {}),
        ready: true,
      };
    }

    case 'DiscardCard': {
      const card = (player?.hand ?? []).find((c) => c.id === command.cardId);
      return {
        key: `discard-${command.cardId}-${command.pile}`,
        command,
        owner: 'panel',
        group: 'action',
        title: `Discard the ${card?.type ?? 'card'} onto pile ${command.pile + 1}`,
        detail: 'it goes face up, where anyone may take it',
        ready: true,
      };
    }

    case 'UseExtraActionTile': {
      const own = player?.extraActionTiles.find((t) => t.id === command.tileId);
      const action = command.action ?? own?.action;
      const twice = action !== undefined && (player?.plus2Spaces ?? []).includes(action);
      return {
        key: `extra-${command.tileId}-${command.action ?? 'own'}`,
        command,
        owner: 'panel',
        group: 'action',
        title: twice
          ? `Take two more ${action} actions`
          : `Take another ${action ?? ''} action`.replace('  ', ' '),
        detail:
          (command.action
            ? 'spends a wild extra-action forum tile'
            : `spends your ${action} extra-action tile`) +
          (twice ? `, and your “+2” marker makes it two` : '') +
          ' — the tile leaves the game',
        ready: true,
      };
    }

    case 'SatisfyDemand': {
      // The tile can be a forum tile of that icon, a wild, or a demand tile
      // kept from the Trajan track (R-021).
      const forum = player?.forumTiles.find((t) => t.id === command.tileId);
      const kept = player?.trajanDemandTiles.find((t) => t.id === command.tileId);
      const what = forum
        ? forum.kind === 'wild-demand'
          ? 'a wild demand tile'
          : `your ${forum.kind === 'demand' ? forum.icon : forum.kind} forum tile`
        : kept
          ? `the ${kept.icon ?? ''} Trajan tile on your mat`.replace('  ', ' ')
          : 'a tile';
      return {
        key: `demand-${command.demandIndex}-${command.tileId}`,
        command,
        owner: 'panel',
        group: 'action',
        title: `Meet it with ${what}`,
        detail: forum ? 'the forum tile is spent' : 'usable once per demand, then again next time',
        ready: true,
      };
    }

    case 'ChooseBonusTile': {
      const tile = view.board.senateBonus[command.side];
      const side = command.side === 0 ? ('yellow' as const) : ('grey' as const);
      if (!tile) {
        return {
          key: `bonus-${command.side}`,
          command,
          owner: 'panel',
          group: 'action',
          title: `Take the ${side} tile`,
          ready: true,
        };
      }
      const worth = player ? bonusTileWorth(view, player, tile, side) : 0;
      return {
        key: `bonus-${command.side}`,
        command,
        owner: 'panel',
        group: 'action',
        title: `Take the ${side} tile: ${bonusCondition(tile, side)}`,
        detail: `scores at the end of the game — worth ${worth} VP as things stand`,
        vp: worth,
        ready: true,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// The actions this slice lays out: forum, senate, trajan.
// ---------------------------------------------------------------------------

function describeAction(
  command: Extract<Command, { type: 'TakeAction' }>,
  view: View,
  seat: PlayerId,
  i: number,
): Option {
  const base = { command, owner: 'panel' as const, group: 'action' as const };
  const player = view.players[seat];

  if (command.kind === 'forum') {
    const p = command.payload as { from: 'forum' | 'yellow'; index: number };
    if (p.from === 'yellow') {
      const tile = view.board.yellowSpaces[p.index];
      return {
        ...base,
        key: `forum-yellow-${p.index}`,
        title: tile ? `Take the ${tile.action} extra-action tile` : 'Take the extra-action tile',
        detail: `yellow space ${p.index + 1} — keep it for a second action on a later turn`,
        ready: true,
      };
    }
    const tile = view.board.forum[p.index];
    return {
      ...base,
      key: `forum-${p.index}`,
      title: tile ? `Take the ${forumName(tile)}` : 'Take the forum tile',
      detail: `forum space ${p.index + 1}${tile ? ` — ${forumUse(tile, view)}` : ''}`,
      ready: true,
    };
  }

  if (command.kind === 'senate') {
    const from = player?.senate ?? 0;
    const space = MANIFESTS.senateTrack.spaces[from + 1];
    const now = MANIFESTS.senateTrack.spaces[from]?.votes ?? 0;
    return {
      ...base,
      key: 'senate',
      // Spaces are named by the number printed on them, as the board shows them,
      // not by their index along the track.
      title: space ? `Advance your disc to the ${space.vp} space` : 'Advance your disc one space',
      detail: space
        ? `${space.votes} votes at the balance of power, against ${now} now`
        : 'one space along the senate track',
      ...(space ? { vp: space.vp } : {}),
      ready: true,
    };
  }

  if (command.kind === 'trajan') {
    const p = command.payload as { stack: number };
    const tile = view.board.trajanStacks[p.stack]?.[0];
    const arch = player?.arch;
    return {
      ...base,
      key: `trajan-${p.stack}`,
      title: tile ? `Take the ${trajanName(tile.kind)} tile` : `Take from stack ${p.stack + 1}`,
      ...(tile
        ? {
            detail:
              `needs ${tile.colors[0]} + ${tile.colors[1]}` +
              (typeof arch === 'number' ? `, lands on slot ${ROMAN[arch]}` : ''),
            vp: tile.vp,
          }
        : {}),
      ready: true,
    };
  }

  if (command.kind === 'military') return describeMilitary(command, view, seat);
  if (command.kind === 'seaport') return describeSeaport(command, view, i);
  if (command.kind === 'construction') return describeConstruction(command, view, seat);

  return {
    ...base,
    key: `${command.kind}-${i}`,
    title: `${command.kind}: ${summarise(command.payload)}`,
    ready: false,
  };
}

/**
 * The construction action. Building names a site, so it is also one the board
 * lets you point at — see `targets.ts`.
 */
function describeConstruction(
  command: Extract<Command, { type: 'TakeAction' }>,
  view: View,
  seat: PlayerId,
): Option {
  const base = { command, owner: 'panel' as const, group: 'action' as const, ready: true };
  const player = view.players[seat];
  const p = command.payload as { option: string; site?: number };

  if (p.option === 'deploy') {
    return {
      ...base,
      key: 'construction-deploy',
      title: 'Deploy a worker to the camp',
      detail: `one of your ${player?.supply ?? 0} tokens left in the supply; ${player?.workers ?? 0} already in camp`,
    };
  }

  const index = p.site ?? 0;
  const site = view.board.sites[index];
  const columns = MANIFESTS.constructionGrid.columns;
  const where = `row ${Math.floor(index / columns) + 1}, column ${(index % columns) + 1}`;
  if (!site?.tile) {
    return {
      ...base,
      key: `construction-build-${index}`,
      title: 'Build on an empty site',
      detail: `${where} — no tile left to take, but it extends where you may build next`,
    };
  }
  const bonus = MANIFESTS.firstOfTypeBonus[site.tile.icon];
  const first = !(player?.constructionTiles ?? []).some((t) => t.icon === site.tile!.icon);
  return {
    ...base,
    key: `construction-build-${index}`,
    title: `Build on the ${site.tile.icon} site`,
    detail:
      `${where}` +
      (bonus && first
        ? ` — your first ${site.tile.icon}, so it also gives you a free ${bonus} action`
        : ` — you already have a ${site.tile.icon}, so no bonus action`),
    vp: site.tile.vp,
  };
}

/**
 * The military options. Moving and occupying name a province, so they are also
 * the ones the board lets you point at — see `targets.ts`.
 */
function describeMilitary(
  command: Extract<Command, { type: 'TakeAction' }>,
  view: View,
  seat: PlayerId,
): Option {
  const base = { command, owner: 'panel' as const, group: 'action' as const, ready: true };
  const player = view.players[seat];
  const p = command.payload as { option: string; province?: ProvinceId };

  if (p.option === 'deploy') {
    return {
      ...base,
      key: 'military-deploy',
      title: 'Deploy a legionnaire to the camp',
      detail: `one of your ${player?.supply ?? 0} tokens left in the supply; ${player?.legionnaires ?? 0} already in camp`,
    };
  }

  if (p.option === 'move' && p.province) {
    const province = MANIFESTS.provinces.find((q) => q.id === p.province);
    const live = view.board.provinces[p.province];
    const others = (live?.legionnaires ?? []).filter((id) => id !== seat).length;
    const here = [
      live?.tile ? 'seizes the forum tile there' : 'no tile left there',
      others > 0 ? `${others} opposing legionnaire${others === 1 ? '' : 's'}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      ...base,
      key: `military-move-${p.province}`,
      title: `Move your leader to ${province?.name ?? p.province}`,
      detail: `worth ${province?.vp ?? 0} VP to occupy — ${here}`,
    };
  }

  if (p.option === 'occupy') {
    const at = player?.leader;
    const province = at && at !== 'camp' ? MANIFESTS.provinces.find((q) => q.id === at) : undefined;
    const vp = at && at !== 'camp' ? occupationVp(view, seat, at) : 0;
    const others =
      at && at !== 'camp'
        ? (view.board.provinces[at]?.legionnaires ?? []).filter((id) => id !== seat).length
        : 0;
    return {
      ...base,
      key: 'military-occupy',
      title: `Occupy ${province?.name ?? 'the province'}`,
      detail:
        others > 0
          ? `${province?.vp ?? 0} VP less ${MANIFESTS.scoring.provinceOpponentPenalty} for each of ${others} opposing legionnaire${others === 1 ? '' : 's'}`
          : `no one else is there, so the province is worth its full value`,
      vp,
    };
  }

  return { ...base, key: 'military-other', title: `military: ${summarise(command.payload)}` };
}

/**
 * The seaport. Drawing and taking from a pile are one-click options; displaying
 * and shipping are handed to the card selector, which can build any payload in
 * those two families rather than the single representative one the engine lists.
 */
function describeSeaport(
  command: Extract<Command, { type: 'TakeAction' }>,
  view: View,
  i: number,
): Option {
  const base = { command, group: 'action' as const, ready: true };
  const p = command.payload as { option: string; pile?: 0 | 1 };

  if (p.option === 'draw') {
    return {
      ...base,
      owner: 'panel',
      key: 'seaport-draw',
      title: 'Draw two cards, then discard one',
      detail: `${view.hiddenCounts.drawPile} cards left in the pile`,
    };
  }

  if (p.option === 'takeDiscard' && (p.pile === 0 || p.pile === 1)) {
    const pile = view.discards[p.pile];
    const top = pile[pile.length - 1];
    return {
      ...base,
      owner: 'panel',
      key: `seaport-discard-${p.pile}`,
      title: top ? `Take the ${top.type} card` : `Take from pile ${p.pile + 1}`,
      detail: `face up on pile ${p.pile + 1}`,
    };
  }

  return {
    ...base,
    owner: 'builder',
    key: `seaport-${p.option}-${i}`,
    title: p.option === 'display' ? 'Display cards' : 'Ship a set',
    detail: 'choose the cards below',
  };
}

export function forumName(tile: NonNullable<View['board']['forum'][number]>): string {
  switch (tile.kind) {
    case 'demand':
      return `${tile.icon} demand tile`;
    case 'senate':
      return `${tile.votes}-vote senate tile`;
    case 'wild-commodity':
      return 'wild commodity tile';
    case 'wild-demand':
      return 'wild demand tile';
    case 'wild-construction':
      return 'wild construction tile';
    case 'wild-extra-action':
      return 'wild extra-action tile';
  }
}

export function forumUse(tile: NonNullable<View['board']['forum'][number]>, view: View): string {
  switch (tile.kind) {
    case 'demand':
      // Eleven rows all saying "meets a demand" tell you nothing; which demand does.
      return view.demands.some((d) => d.icon === tile.icon)
        ? `meets the ${tile.icon} demand already showing`
        : `no ${tile.icon} demand is showing yet this quarter`;
    case 'senate':
      return 'adds to your votes at the balance of power';
    case 'wild-commodity':
      return 'stands in for any commodity card in a shipped set';
    case 'wild-demand':
      return 'meets a demand of any kind';
    case 'wild-construction':
      return 'stands in for any construction tile in a set';
    case 'wild-extra-action':
      return 'stands in for an extra-action tile of any action';
  }
}

/** What accomplishing a Trajan tile gives you, beyond its VP. */
export function trajanReward(tile: { kind: string; tokens?: number; icon?: string }): string {
  switch (tile.kind) {
    case 'cards':
      return `draw ${MANIFESTS.trajanRewards.cards} cards`;
    case 'vp':
      return 'points and nothing else';
    case 'workers':
      return `deploy ${tile.tokens ?? 1} worker${tile.tokens === 1 ? '' : 's'} to your camp`;
    case 'legionnaires':
      return `deploy ${tile.tokens ?? 1} legionnaire${tile.tokens === 1 ? '' : 's'} to your camp`;
    case 'plus2':
      return 'choose an extra-action space for the “+2” marker';
    case 'demand':
      return `kept on your mat: it meets a ${tile.icon ?? ''} demand at a quarter end`.trim();
    default:
      return 'takes it off your mat';
  }
}

const TRAJAN_NAME: Record<string, string> = {
  cards: 'two cards',
  vp: '9 VP',
  workers: 'workers',
  legionnaires: 'legionnaires',
  plus2: '“+2” marker',
  demand: 'demand',
};
export const trajanName = (kind: string) => TRAJAN_NAME[kind] ?? kind;

const STACK_TITLE: Record<string, string> = {
  cards: 'Cards',
  vp: 'VP',
  workers: 'Workers',
  legionnaires: 'Legionnaires',
  plus2: '“+2”',
  demand: 'Demand',
};
/** A Trajan stack's name, as the board labels it. */
export const stackTitle = (kind: string) => STACK_TITLE[kind] ?? kind;

/** A readable shape for a payload whose own panel is not built yet. */
function summarise(payload: unknown): string {
  if (payload === null || payload === undefined) return 'do it';
  if (typeof payload !== 'object') return String(payload);
  const parts = Object.entries(payload as Record<string, unknown>).map(([k, v]) =>
    Array.isArray(v) ? `${k} ${v.join(', ')}` : `${k} ${String(v)}`,
  );
  return parts.length > 0 ? parts.join(' · ') : 'do it';
}

/** One row in the panel: an option, and any interchangeable twins it stands for. */
export interface Row {
  option: Option;
  /** How many legal commands this row covers — more than one when they are alike. */
  count: number;
  commands: Command[];
}

/**
 * Collapse options that read identically into one row.
 *
 * Two wild extra-action tiles, or two forum tiles of the same icon, are
 * different commands and the same choice: spending either has exactly the same
 * effect, and the player cannot tell them apart because there is nothing to
 * tell. Showing both is not more honest, it is just two identical buttons. So
 * rows are grouped by what they actually say, every legal command lands in
 * exactly one row, and a row that stands for several says so.
 */
export function mergeOptions(options: readonly Option[]): Row[] {
  const rows: Row[] = [];
  const byText = new Map<string, Row>();
  for (const option of options) {
    const text = `${option.title}\u0000${option.detail ?? ''}`;
    const existing = byText.get(text);
    if (existing) {
      existing.count++;
      existing.commands.push(option.command);
      continue;
    }
    const row: Row = { option, count: 1, commands: [option.command] };
    byText.set(text, row);
    rows.push(row);
  }
  return rows;
}

type BonusTile = NonNullable<View['board']['senateBonus'][number]>;

/** What a bonus tile pays for, in a phrase. */
export function bonusCondition(tile: BonusTile, side: 'yellow' | 'grey'): string {
  const rate = side === 'yellow' ? tile.yellow : tile.grey;
  switch (tile.kind) {
    case 'demand-icon':
      return `${rate} VP if you hold a ${tile.icon ?? ''} forum tile`.replace('  ', ' ');
    case 'per-display-card':
      return `${rate} VP for each ${(tile.commodities ?? []).join(' or ')} card on display`;
    case 'per-worker-on-site':
      return Number.isInteger(rate)
        ? `${rate} VP for each worker of yours on a site`
        : 'a point for every two workers of yours on a site';
    case 'per-legionnaire-in-province':
      return `${rate} VP for each legionnaire of yours in a province`;
    case 'per-yellow-tile':
      return `${rate} VP for each yellow bonus tile you end with`;
    default:
      return `${rate} VP a time`;
  }
}

/**
 * What taking this tile would add to your final score at today's board. Worked
 * out by asking the engine's own `bonusTileVp` twice, so the number offered is
 * the number the game will pay.
 */
function bonusTileWorth(
  view: View,
  player: View['players'][number],
  tile: BonusTile,
  side: 'yellow' | 'grey',
): number {
  const withHand = { ...player, hand: player.hand ?? [] };
  return (
    bonusTileVp(view, { ...withHand, bonusTiles: [...withHand.bonusTiles, { ...tile, side }] }) -
    bonusTileVp(view, withHand)
  );
}

/** What the game is waiting for, in a sentence. */
export function prompt(view: View): { title: string; hint: string } {
  const phase = view.phase;
  switch (phase.kind) {
    case 'setup.arrangeMarkers':
      return {
        title: 'Setup',
        hint: 'Arrange your twelve markers on the mat — where they start is where your game starts.',
      };
    case 'setup.chooseTrajanTiles':
      return {
        title: 'Setup',
        hint: 'Take three Trajan tiles from three different stacks, one per category.',
      };
    case 'awaitTray':
      return { title: 'Your turn', hint: 'Pick up a tray on your mat to start the turn.' };
    case 'awaitTrajanDecision':
      return {
        title: 'Trajan tile',
        hint: `The tile in slot ${ROMAN[phase.slot]} is satisfied. Accomplish it, or leave it.`,
      };
    case 'awaitAction':
      return { title: capitalise(phase.action), hint: actionHint(phase.action) };
    case 'awaitSeaportDiscard':
      return { title: 'Seaport', hint: 'Discard one card onto either pile.' };
    case 'awaitExtraAction':
      return {
        title: 'Extra action',
        hint: 'Spend an extra-action tile to take an action again, or end your turn.',
      };
    case 'quarterEnd.demands':
      return {
        title: 'The people demand',
        hint: 'Spend a tile to meet this demand. An unmet demand costs points.',
      };
    case 'quarterEnd.consulChoice':
      return { title: 'Consul', hint: 'You have the most votes: take a bonus tile.' };
    case 'gameOver':
      return { title: 'Game over', hint: 'The final scores are laid over the table.' };
  }
}

function actionHint(action: ActionKind): string {
  switch (action) {
    case 'forum':
      return 'Take one face-up tile from the forum or a yellow space.';
    case 'senate':
      return 'Move your disc one space along the senate track and score it.';
    case 'trajan':
      return 'Take the top tile of a stack onto the slot the Arch marks.';
    case 'military':
      return 'Deploy a legionnaire, move your leader, or occupy a province.';
    case 'seaport':
      return 'Draw, take from a discard pile, display cards, or ship a set.';
    case 'construction':
      return 'Deploy a worker, or build on a site in the district.';
  }
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const article = A;
